import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";

/**
 * Report filters live in the URL query string, so a filtered view survives a reload
 * and can be shared. Every value is a string; a value equal to its default (or "")
 * is dropped from the URL. Changing any filter other than `page` resets to page 1.
 *
 * `defaults` must be a module-level constant -- it is read on every render.
 */
export function useUrlFilters<T extends Record<string, string>>(defaults: T) {
  const [sp, setSp] = useSearchParams();

  const values = useMemo(() => {
    const out: Record<string, string> = { ...defaults };
    for (const k of Object.keys(defaults)) {
      const v = sp.get(k);
      if (v != null) out[k] = v;
    }
    return out as T;
  }, [sp, defaults]);

  const set = useCallback((patch: Partial<T>) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === "" || v === defaults[k]) next.delete(k);
        else next.set(k, String(v));
      }
      if (!("page" in patch)) next.delete("page");
      return next;
    }, { replace: true });
  }, [setSp, defaults]);

  return [values, set] as const;
}

/** Search box text, pushed to the filters 300 ms after typing stops. */
export function useDebouncedSearch(value: string, onCommit: (v: string) => void) {
  const [text, setText] = useState(value);
  // Held in a ref so an inline callback doesn't restart the timer on every render.
  const commit = useRef(onCommit);
  commit.current = onCommit;
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (text.trim() === value) return;
    const t = setTimeout(() => commit.current(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text, value]);
  return [text, setText] as const;
}

/** Turns a filter object into query params, leaving out empty values. */
export function toParams(values: Record<string, string>, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...values, ...extra })) if (v !== "") p.set(k, v);
  return p;
}

/** Loads one report page; refetches whenever the query string changes. */
export function useReportData<T>(path: string, params: URLSearchParams | null, source: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const qs = params?.toString() ?? null;

  useEffect(() => {
    if (qs == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get<T>(`${path}?${qs}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        logError(`${source}.load`, err);
        setError(errMsg(err) ?? "Failed to load the report");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, qs, source]);

  return { data, loading, error };
}

/** Params only the table uses; the cards request leaves them out so they never refetch it. */
const ROW_ONLY_PARAMS = ["page", "limit", "sortBy", "sortDir", "columns"];

/**
 * A report as two requests: the table page (`part=rows`, paged in the database) and the
 * summary cards (`part=cards`). The cards' query string has no page / sort / column
 * params -- nor the `rowOnly` filters that narrow only the table -- so turning a page,
 * sorting or changing page size reloads just the rows.
 */
export function useReport<T extends { cards?: unknown }>(
  path: string,
  params: URLSearchParams | null,
  source: string,
  rowOnly: readonly string[] = [],
) {
  const rowParams = useMemo(() => {
    if (!params) return null;
    const p = new URLSearchParams(params);
    p.set("part", "rows");
    return p;
  }, [params]);
  const cardParams = useMemo(() => {
    if (!params) return null;
    const p = new URLSearchParams(params);
    for (const k of [...ROW_ONLY_PARAMS, ...rowOnly]) p.delete(k);
    p.set("part", "cards");
    return p;
  }, [params, rowOnly]);
  const rows = useReportData<T>(path, rowParams, source);
  const cards = useReportData<T>(path, cardParams, `${source}.cards`);
  return {
    data: rows.data,
    cards: cards.data?.cards as T["cards"] | undefined,
    loading: rows.loading,
    error: rows.error ?? cards.error,
  };
}

export interface OrgMeta {
  offices: Array<{ id: string; name: string }>;
  departments: Array<{ id: string; name: string; office?: { id: string; name: string } | null }>;
}

export function useOrgMeta(): OrgMeta {
  const [meta, setMeta] = useState<OrgMeta>({ offices: [], departments: [] });
  useEffect(() => {
    api.get<OrgMeta>("/employees/org-meta")
      .then((r) => setMeta(r.data))
      .catch((err) => logError("reports.orgMeta", err));
  }, []);
  return meta;
}

/**
 * Downloads the report as .xlsx through the axios instance (so the 401 refresh still
 * applies). A 413 "narrow your filters" comes back as a JSON blob; its message is
 * thrown rather than saved as a broken workbook.
 */
export async function downloadReport(path: string, params: URLSearchParams, filename: string): Promise<void> {
  const p = new URLSearchParams(params);
  p.delete("page");
  p.delete("limit");
  p.delete("part");
  p.set("format", "xlsx");
  try {
    const res = await api.get(`${path}?${p}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    const blob = (err as { response?: { data?: unknown } }).response?.data;
    if (blob instanceof Blob) {
      let body: { error?: string; message?: string } = {};
      try { body = JSON.parse(await blob.text()) as typeof body; } catch { /* not JSON */ }
      throw new Error(body.error ?? body.message ?? "Export failed");
    }
    throw err;
  }
}
